// Sign in to web chat with a code sent by email (ADR-0011). The person gives only their email: the code goes to every
// business it may sign in to (ADR-0034), and when it is right for several they choose one by its name. A business's own
// link may still name it as ?business=…, which the code is sent for too. Before sign-in the page speaks the browser's
// language, and offers the switch between Hindi and English.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { ApiError, type Api, type Session } from "../api.js";
import { otherLanguage, useLanguage, useWords } from "../i18n/words.js";
import type { BusinessChoice, SignInApi } from "./api.js";

/** The business a link names, if any: `?business=<id>`. */
const linkedBusiness = (): string | undefined =>
  new URLSearchParams(window.location.search).get("business")?.trim() || undefined;

export function SignIn({
  api,
  signIn,
  onSignedIn,
}: {
  api: Pick<Api, "session">;
  signIn: SignInApi;
  onSignedIn: (session: Session) => void;
}) {
  const w = useWords();
  const { language, setLanguage } = useLanguage();
  const [business] = useState(linkedBusiness);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(false);
  const [step, setStep] = useState<"email" | "code" | "choose">("email");
  const [choices, setChoices] = useState<readonly BusinessChoice[]>([]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const codeInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (step === "code") codeInput.current?.focus();
  }, [step]);

  const requestCode = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    try {
      await signIn.requestCode(email.trim(), business);
      setStep("code");
    } catch (error) {
      setProblem(error instanceof ApiError && error.status === 429 ? w.signInTooMany : w.signInCheck);
    } finally {
      setBusy(false);
    }
  };

  /** Checks the code, across every business or in the one chosen, and opens the session it starts. */
  const check = async (chosen?: string) => {
    setBusy(true);
    setProblem(undefined);
    try {
      const checked = await signIn.verifyCode(email.trim(), code.trim(), trust, chosen ?? business);
      if ("choose" in checked) {
        setChoices(checked.choose);
        setStep("choose");
        return;
      }
      const session = await api.session();
      if (session) onSignedIn(session);
      else setProblem(w.signInNotKept);
    } catch {
      setProblem(w.signInBadCode);
      setStep("code");
      codeInput.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="sign-in" id="main">
      <p className="sign-in-language">
        <button type="button" className="chip" onClick={() => setLanguage(otherLanguage(language))}>
          {w.languageSwitch}
        </button>
      </p>
      <h1>{step === "choose" ? w.signInChoose : w.signInTitle}</h1>
      {step === "email" ? (
        <form onSubmit={requestCode}>
          <label className="field">
            {w.signInEmail}
            <input
              type="email"
              value={email}
              required
              autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <button type="submit" className="btn btn-go" disabled={busy}>
            {w.signInEmailMe}
          </button>
        </form>
      ) : step === "code" ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void check();
          }}
        >
          <p>{w.signInSent(email)}</p>
          <label className="field">
            {w.signInCode}
            <input
              ref={codeInput}
              value={code}
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              onChange={(e) => setCode(e.target.value)}
            />
          </label>
          <label className="check">
            <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} />
            {w.signInTrust}
          </label>
          <button type="submit" className="btn btn-go" disabled={busy}>
            {w.signInSubmit}
          </button>
          <button type="button" className="linkish" onClick={() => setStep("email")}>
            {w.signInOtherEmail}
          </button>
        </form>
      ) : (
        <>
          <p>{w.signInChooseIntro}</p>
          <ul className="sign-in-choices">
            {choices.map((choice) => (
              <li key={choice.tenantId}>
                <button type="button" className="btn" disabled={busy} onClick={() => void check(choice.tenantId)}>
                  {choice.name ?? w.shellBusiness}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
    </main>
  );
}
